import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { BlockStack, Card, Page, Spinner, Tabs, Text } from '@shopify/polaris';
import { useNavigate, useParams } from 'react-router-dom';
import { useAxiosInstance } from '../../context/AxiosContext';
import { usePromotionRecord, APPS, CARDS, BANNERS } from './api';
import { useToast } from './Layout';
import CardsList from './CardsList';
import BannersList from './BannersList';
import Arrangement from './Arrangement';
import CardEditor from './CardEditor';
import BannerEditor from './BannerEditor';

export const CardsScreen = () => {
  const navigate = useNavigate();

  return (
    <Page
      fullWidth
      title="Partner cards"
      subtitle="The grid of partner apps merchants see on the Partner apps page"
      primaryAction={{ content: 'Add partner card', onAction: () => navigate('/promotions/cards/new') }}
    >
      <CardsList onEdit={(card) => navigate(`/promotions/cards/${card.id}/edit`)} />
    </Page>
  );
};

export const BannersScreen = () => {
  const navigate = useNavigate();

  return (
    <Page
      fullWidth
      title="Featured banners"
      subtitle="The wide callout above the grid. Several can run at once, each with its own dates."
      primaryAction={{ content: 'Add featured banner', onAction: () => navigate('/promotions/banners/new') }}
    >
      <BannersList onEdit={(banner) => navigate(`/promotions/banners/${banner.id}/edit`)} />
    </Page>
  );
};

export const OrderScreen = () => {
  const [selected, setSelected] = useState(0);
  const app = APPS[selected];

  return (
    <Page
      fullWidth
      title="Display order"
      subtitle="Each app has its own order. Moving something here changes only that app."
    >
      <BlockStack gap="400">
        <Card padding="0">
          <Tabs
            tabs={APPS.map(({ code, name }) => ({ id: code, content: name }))}
            selected={selected}
            onSelect={setSelected}
            fitted={false}
          />
        </Card>

        <Arrangement
          resource={BANNERS}
          title="Featured banners"
          appCode={app.code}
          appName={app.name}
        />
        <Arrangement
          resource={CARDS}
          title="Partner cards"
          appCode={app.code}
          appName={app.name}
        />
      </BlockStack>
    </Page>
  );
};

const EditorScreen = ({ resource, backTo, Editor, propName }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const setToast = useToast();
  const axiosInstance = useAxiosInstance();
  const { record, isLoading, isError } = usePromotionRecord({ resource, id, axiosInstance });

  const close = (message) => {
    if (message) setToast(message);
    navigate(backTo);
  };

  if (id && isLoading) {
    return (
      <Page fullWidth>
        <BlockStack inlineAlign="center"><Spinner accessibilityLabel="Loading" /></BlockStack>
      </Page>
    );
  }

  if (id && isError) {
    return (
      <Page fullWidth backAction={{ content: 'Back', onAction: () => navigate(backTo) }} title="Could not load">
        <Text as="p" tone="subdued">The promotion could not be loaded. Reload to try again.</Text>
      </Page>
    );
  }

  if (id && !record) {
    return (
      <Page fullWidth backAction={{ content: 'Back', onAction: () => navigate(backTo) }} title="Not found">
        <Text as="p" tone="subdued">That promotion no longer exists, or has been archived.</Text>
      </Page>
    );
  }

  return <Editor key={id || "new"} {...{ [propName]: record || {} }} onClose={close} />;
};

EditorScreen.propTypes = {
  resource: PropTypes.string.isRequired,
  backTo: PropTypes.string.isRequired,
  Editor: PropTypes.elementType.isRequired,
  propName: PropTypes.string.isRequired,
};

export const CardEditorScreen = () => (
  <EditorScreen resource={CARDS} backTo="/promotions/cards" Editor={CardEditor} propName="card" />
);

export const BannerEditorScreen = () => (
  <EditorScreen resource={BANNERS} backTo="/promotions/banners" Editor={BannerEditor} propName="banner" />
);
